import React, { useContext, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, Alert, CircularProgress
} from "@mui/material";
import api_endpoints from "../../../api";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import AuthContext from "../../../context/AuthContext/AuthContext";
import { useSuccessAlert } from "../../../context/SuccessAlertContext/SuccessAlertContext";

export const AddCustomerModal = ({ onClose, onSave, organizationId }) => {
  const { t } = useCustomTranslation("Recurring Payment");
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [apt, setApt] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [zip, setZip] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const addressLine = [street, apt, city, province, zip].filter(Boolean).join(", ");

  function buildCreateDonorErrorMessage(err) {
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.message;

    switch (status) {
      case 400:
        return t("email_is_required");
      case 409:
        return t("email_already_used_for_this_organization", {
          defaultValue: "Email already used for this organization",
        });
      case 422:
        return t("invalid_email_syntax", {
          defaultValue: "Invalid email syntax",
        });
      default:
        if (typeof apiMessage === "string" && apiMessage.trim()) {
          return apiMessage;
        }
        return err?.message || t("failed_to_create_donor");
    }
  }

  function normalizeCreatedDonor(response, payload) {
    const donor = response?.data;
    const donorId = donor?._id || donor?.id;

    if (!donorId) {
      throw new Error(
        t("failed_to_select_created_customer", {
          defaultValue: "Customer was created, but no customer ID was returned.",
        })
      );
    }

    return {
      ...payload,
      ...donor,
      id: donorId,
      _id: donorId,
    };
  }

  async function handleSubmit() {
    setError("");
    if (!email.trim()) {
      setError(t("email_is_required"));
      return;
    }
    if (typeof onSave !== "function") {
      setError(
        t("failed_to_select_created_customer", {
          defaultValue: "Customer could not be selected. Please try again.",
        })
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: first || "",
        last_name: last || "",
        zip_code: zip || "",
        email: email || "",
        phone: phone || "",
        adresse: addressLine,
        company: "",
        ...(organizationId ? { organization_id: organizationId } : {}),
      }
      const response = await api_endpoints.createDonor(payload);
      const normalizedDonor = normalizeCreatedDonor(response, payload);

      const saveResult = await onSave(normalizedDonor);
      if (saveResult !== true) {
        setError(
          t("failed_to_select_created_customer", {
            defaultValue: "Customer was created, but could not be selected.",
          })
        );
        return;
      }

      setMessageSuccessAlert(
        t("customer_created_successfully", {
          defaultValue: "Customer created successfully",
        })
      );
      setShowSuccessAlert(true);

    } catch (e) {
      setError(buildCreateDonorErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("add_new_customer")}</DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("first_name")}
              fullWidth
              value={first}
              onChange={(e) => setFirst(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("last_name")}
              fullWidth
              value={last}
              onChange={(e) => setLast(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t("phone_number")}
              fullWidth
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              label={t("email")}
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("street_address")}
              fullWidth
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t("apt_suite")}
              fullWidth
              value={apt}
              onChange={(e) => setApt(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t("city_town")}
              fullWidth
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t("province")}
              fullWidth
              value={province}
              onChange={(e) => setProvince(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={t("zip")}
              fullWidth
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClose} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
